import { useElementSize } from '@agilelab/plugin-wb-platform';
import { makeStyles, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { CSSProperties, FC, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import {
  PrototypeInformationGraphContextProvider,
  usePrototypeInformationGraphContext,
} from './PrototypeInformationGraphContext';
import {
  PROTOTYPE_EDGE_ID,
  PrototypeEdge,
} from './ReactFlow/edges/PrototypeEdge';
import { buildPrototypeEdges, buildPrototypeNodes } from './ReactFlow/layout';
import {
  BUSINESS_CONCEPTS_NODE_ID,
  BusinessConcepts,
} from './ReactFlow/nodes/BusinessConcepts/BusinessConcepts';
import {
  DATA_SOURCES_NODE_ID,
  DataSources,
} from './ReactFlow/nodes/DataSources/DataSources';
import {
  HEXAGON_SIZE,
  PROTOTYPE_NODE_ID,
  PrototypeNode,
} from './ReactFlow/nodes/PrototypeNode/PrototypeNode';
import { Prototype } from './types';

type PrototypeInformationGraphProps = {
  className?: string;
  style?: CSSProperties;
  prototype: Prototype;
};

const useStyles = makeStyles(
  theme => ({
    root: {
      width: '100%',
      height: '100%',
      // disables grab cursor from react flow background
      '& .react-flow__pane': {
        cursor: 'default',
      },
      // to enable scrolling the main page when mouse is on background
      '& .react-flow__renderer': {
        pointerEvents: 'none',
      },
    },
    headerActions: {
      display: 'flex',
      justifyContent: 'center',
      gap: theme.spacing(1),
    },
  }),
  { name: 'PrototypeInformationGraph' },
);

export const NODE_TYPES = {
  [PROTOTYPE_NODE_ID]: PrototypeNode,
  [BUSINESS_CONCEPTS_NODE_ID]: BusinessConcepts,
  [DATA_SOURCES_NODE_ID]: DataSources,
};

export const EDGES_TYPES = {
  [PROTOTYPE_EDGE_ID]: PrototypeEdge,
};

export const PrototypeInformationContent: FC<
  PrototypeInformationGraphProps
> = ({ className, style }) => {
  const theme = useTheme();

  const classes = useStyles();

  const { visibleBusinessConcepts, visibleDataSources } =
    usePrototypeInformationGraphContext();

  const pageRef = useRef<HTMLDivElement>(null);

  // measure viewport size
  const { clientHeight, clientWidth } = useElementSize(pageRef, 10);

  const { project } = useReactFlow();

  const nodes = useMemo(() => {
    if (!clientHeight || !clientWidth) return [];
    return buildPrototypeNodes(
      clientHeight,
      clientWidth,
      0,
      HEXAGON_SIZE,
      project,
    );
  }, [clientHeight, clientWidth, project]);

  const edges = useMemo(() => {
    return buildPrototypeEdges(
      visibleBusinessConcepts,
      visibleDataSources,
      theme,
    );
  }, [theme, visibleBusinessConcepts, visibleDataSources]);

  return (
    <div ref={pageRef} className={clsx(classes.root, className)} style={style}>
      <ReactFlow
        proOptions={{ hideAttribution: true }}
        edgesUpdatable={false}
        edgesFocusable={false}
        nodesFocusable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        zoomOnScroll={false}
        panOnDrag={false}
        minZoom={1}
        maxZoom={1}
        nodes={nodes}
        draggable={false}
        edges={edges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGES_TYPES}
      >
        <Background variant={BackgroundVariant.Cross} />
      </ReactFlow>
    </div>
  );
};

export const PrototypeInformationGraph: FC<PrototypeInformationGraphProps> = ({
  ...props
}) => {
  const [visibleBusinessConcepts, setVisibleBusinessConcepts] = useState<
    string[]
  >([]);

  const [visibleDataSources, setVisibleDataSources] = useState<string[]>([]);

  return (
    <PrototypeInformationGraphContextProvider
      value={{
        visibleDataSources,
        setVisibleDataSources,
        visibleBusinessConcepts,
        setVisibleBusinessConcepts,
        prototype: props.prototype,
      }}
    >
      <ReactFlowProvider>
        <PrototypeInformationContent {...props} />
      </ReactFlowProvider>
    </PrototypeInformationGraphContextProvider>
  );
};
